import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useEntityRelated, type RelatedData } from "@/hooks/use-entity-related"
import { NotesTab } from "@/components/notes-tab"
import { ActivityTimeline } from "@/components/activity-timeline"
import {
  ContactsList,
  CompaniesList,
  DealsList,
  TasksList,
  ServiceTicketsList,
  DocumentsList,
  QuotesList,
  InvoicesList,
} from "@/components/related-entities-list"

export interface TabConfig {
  key: string
  label: string
  countKey?: keyof RelatedData
}

interface EntityTabsProps {
  entityType: string
  entityId: string
  tabs: TabConfig[]
  defaultTab?: string
  /** Controlled active tab — when set, the component becomes controlled */
  activeTab?: string
  /** Callback fired when the user changes tabs */
  onTabChange?: (tab: string) => void
  /** Extra tab content injected by the parent page (e.g. deal products/contacts) */
  extraTabContent?: Record<string, React.ReactNode>
}

function CountBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
      {count}
    </span>
  )
}

export function EntityTabs({
  entityType,
  entityId,
  tabs,
  defaultTab,
  activeTab,
  onTabChange,
  extraTabContent,
}: EntityTabsProps) {
  const { data: relatedData, isLoading } = useEntityRelated(entityType, entityId)
  const related = relatedData?.data

  function getCount(countKey: keyof RelatedData | undefined): number {
    if (!countKey || !related) return 0
    const section = related[countKey]
    if (!section) return 0
    return section.count ?? 0
  }

  if (isLoading) {
    return (
      <div className="space-y-4 mt-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  // Support both controlled (activeTab) and uncontrolled (defaultTab) modes
  const tabsProps = activeTab
    ? { value: activeTab, onValueChange: onTabChange }
    : { defaultValue: defaultTab ?? tabs[0]?.key ?? "notes" }

  return (
    <Tabs {...tabsProps} className="mt-6">
      <TabsList className="flex-wrap">
        {tabs.map((tab) => (
          <TabsTrigger key={tab.key} value={tab.key}>
            {tab.label}
            <CountBadge count={getCount(tab.countKey)} />
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent key={tab.key} value={tab.key} className="mt-4">
          {/* Check for extra injected content first */}
          {extraTabContent?.[tab.key] ? (
            extraTabContent[tab.key]
          ) : (
            <TabContentRenderer
              tabKey={tab.key}
              entityType={entityType}
              entityId={entityId}
              related={related}
            />
          )}
        </TabsContent>
      ))}
    </Tabs>
  )
}

function TabContentRenderer({
  tabKey,
  entityType,
  entityId,
  related,
}: {
  tabKey: string
  entityType: string
  entityId: string
  related: RelatedData | undefined
}) {
  switch (tabKey) {
    case "notes":
      return <NotesTab entityType={entityType} entityId={entityId} />
    case "activities":
      return <ActivityTimeline entityType={entityType} entityId={entityId} />
    case "contacts":
      return (
        <div className="surface-card overflow-hidden">
          <ContactsList items={related?.contacts?.items ?? []} />
        </div>
      )
    case "companies":
      return (
        <div className="surface-card overflow-hidden">
          <CompaniesList items={related?.companies?.items ?? []} />
        </div>
      )
    case "deals":
      return (
        <div className="surface-card overflow-hidden">
          <DealsList items={related?.deals?.items ?? []} />
        </div>
      )
    case "tasks":
      return (
        <div className="surface-card overflow-hidden">
          <TasksList items={related?.tasks?.items ?? []} />
        </div>
      )
    case "service_tickets":
      return (
        <div className="surface-card overflow-hidden">
          <ServiceTicketsList items={related?.service_tickets?.items ?? []} />
        </div>
      )
    case "documents":
      return (
        <div className="surface-card overflow-hidden">
          <DocumentsList items={related?.documents?.items ?? []} />
        </div>
      )
    case "quotes":
      return (
        <div className="surface-card overflow-hidden">
          <QuotesList items={related?.quotes?.items ?? []} />
        </div>
      )
    case "invoices":
      return (
        <div className="surface-card overflow-hidden">
          <InvoicesList items={related?.invoices?.items ?? []} />
        </div>
      )
    default:
      return <div className="text-sm py-8 text-center text-muted-foreground">Brak danych</div>
  }
}
